/**
 * blockStore.ts
 *
 * Fine-grained in-memory block store — Step 1 of BLOCK_STORE_REDESIGN.md.
 *
 * The problem this solves: both existing block sources are all-or-nothing.
 * The React context re-renders every consumer on every block change, and each
 * Dexie useLiveBlocks() call re-runs a full-workspace query per mounted
 * component. This store is the per-entity alternative: a component subscribed
 * to ONE block (or one parent's child list) is notified only when THAT slice
 * changes.
 *
 * Current role (Step 1): write-only mirror. GlobalBlockProvider dual-writes
 * every context mutation into this store; nothing reads it yet. Consumers
 * arrive in Step 2+ via useBlock / useChildBlocks / usePages.
 *
 * Design rules (each exists to satisfy useSyncExternalStore or to keep this
 * mirror trustworthy):
 *
 *  • Children are DERIVED from `parent.blockIds` at read time — there is no
 *    separate parent→children index that could diverge from the context,
 *    whose getChildren() reads the same field.
 *  • Snapshot caches are invalidated SYNCHRONOUSLY at write time, so an
 *    imperative read right after a write sees fresh data. Only the
 *    NOTIFICATIONS are deferred (coalesced per microtask), so a 500-block
 *    bulk upsert produces one notification wave, not 500.
 *  • getBlock / getChildBlocks / getPages return identity-stable references
 *    between writes. useSyncExternalStore calls getSnapshot on every render
 *    and bails out on identity equality — an unstable reference would loop.
 *  • A child's CONTENT change refreshes the parent's cached child snapshot
 *    (so later reads return fresh objects) but does NOT notify the parent's
 *    child-list subscribers. Content reactivity belongs to useBlock(childId);
 *    child-list subscriptions are structural (membership + order). This is
 *    what keeps typing inside a block from re-rendering the whole tree level.
 *  • Listener callbacks are isolated — one throwing subscriber cannot break
 *    the flush for the others, and no store write path can throw into the
 *    context mutator that called it.
 *
 * No React, no window, no Dexie imports — safe to import from anywhere,
 * including SSR paths and unit tests.
 */

import type { Block } from "@/types/block";

type Listener = () => void;
type Unsubscribe = () => void;

/** Shared frozen sentinel so "no children" reads are identity-stable. */
const EMPTY_BLOCKS: readonly Block[] = Object.freeze([]);

/** The "pages" slice covers everything the sidebar can surface as a row:
 *  real pages and boards. Content blocks (paragraphs etc.) never qualify,
 *  which is what keeps typing from invalidating the slice. */
function isPageLike(block: Block): boolean {
  return block.blockType === "page" || block.blockType === "collection_view";
}

function addKeyedListener(
  registry: Map<string, Set<Listener>>,
  key: string,
  listener: Listener,
): Unsubscribe {
  let set = registry.get(key);
  if (!set) {
    set = new Set();
    registry.set(key, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0) registry.delete(key);
  };
}

/** Notify a listener set, isolating subscriber errors from each other. */
function emit(listeners: Iterable<Listener> | undefined): void {
  if (!listeners) return;
  for (const listener of listeners) {
    try {
      listener();
    } catch (err) {
      console.error("[blockStore] subscriber threw during notify:", err);
    }
  }
}

export class BlockStore {
  private blocks = new Map<string, Block>();

  // ── Subscriptions (per-key granularity) ─────────────────────────
  private blockListeners = new Map<string, Set<Listener>>();
  private childListeners = new Map<string, Set<Listener>>();
  private pageListeners = new Set<Listener>();

  // ── Snapshot caches (identity-stable between writes) ────────────
  private childSnapshots = new Map<string, readonly Block[]>();
  private pagesSnapshot: readonly Block[] | null = null;

  // ── Coalesced-notification state ────────────────────────────────
  private dirtyBlocks = new Set<string>();
  private dirtyChildLists = new Set<string>();
  private pagesDirty = false;
  private notifyAllPending = false;
  private flushScheduled = false;

  // ─── Reads ───────────────────────────────────────────────────────

  getBlock(blockId: string): Block | undefined {
    return this.blocks.get(blockId);
  }

  /**
   * Ordered child blocks of a parent, per the parent's `blockIds` (children
   * missing from the store are skipped — same filtering the context's
   * getChildrenBlocks applies). Cached per parent until a write touches it.
   */
  getChildBlocks(parentId: string): readonly Block[] {
    let snapshot = this.childSnapshots.get(parentId);
    if (!snapshot) {
      snapshot = this.buildChildSnapshot(parentId);
      this.childSnapshots.set(parentId, snapshot);
    }
    return snapshot;
  }

  /** All sidebar-surfaceable blocks — pages AND boards (collection_view).
   *  Cached until one of them is written or removed. Consumers filter down
   *  (e.g. blockType === "page") as needed. */
  getPages(): readonly Block[] {
    if (!this.pagesSnapshot) {
      const pages: Block[] = [];
      for (const block of this.blocks.values()) {
        if (isPageLike(block)) pages.push(block);
      }
      this.pagesSnapshot = pages;
    }
    return this.pagesSnapshot;
  }

  /** Debug/diagnostics accessor (used by the dev-only mirror check). */
  getBlockCount(): number {
    return this.blocks.size;
  }

  private buildChildSnapshot(parentId: string): readonly Block[] {
    const ids = this.blocks.get(parentId)?.blockIds;
    if (!ids || ids.length === 0) return EMPTY_BLOCKS;
    const out: Block[] = [];
    for (const id of ids) {
      const child = this.blocks.get(id);
      if (child) out.push(child);
    }
    return out.length > 0 ? out : EMPTY_BLOCKS;
  }

  // ─── Subscriptions ───────────────────────────────────────────────

  /** Notified when this specific block is upserted or removed. */
  subscribeBlock(blockId: string, listener: Listener): Unsubscribe {
    return addKeyedListener(this.blockListeners, blockId, listener);
  }

  /**
   * Notified on STRUCTURAL changes to a parent's child list — membership,
   * order, reparenting — not on a child's content edits (use subscribeBlock
   * on the child for that).
   */
  subscribeChildren(parentId: string, listener: Listener): Unsubscribe {
    return addKeyedListener(this.childListeners, parentId, listener);
  }

  /** Notified when any page-type block is added, removed, or changed. */
  subscribePages(listener: Listener): Unsubscribe {
    this.pageListeners.add(listener);
    return () => {
      this.pageListeners.delete(listener);
    };
  }

  // ─── Writes ──────────────────────────────────────────────────────

  /**
   * Insert or replace one block. Callers must pass the FULL merged block
   * (the context's `{ ...existing, ...updates }` result), never a partial.
   */
  upsert(block: Block): void {
    const prev = this.blocks.get(block._id);
    if (prev === block) return; // identical reference — nothing changed

    this.blocks.set(block._id, block);
    this.dirtyBlocks.add(block._id);

    if (isPageLike(block) || (prev && isPageLike(prev))) {
      this.invalidatePages();
    }

    const prevParent = prev?.parentId ?? null;
    const nextParent = block.parentId ?? null;
    if (prevParent !== nextParent) {
      // Reparent: structural for both sides.
      if (prevParent !== null) this.invalidateChildList(prevParent, true);
      if (nextParent !== null) this.invalidateChildList(nextParent, true);
    } else if (nextParent !== null) {
      // Content-only change: refresh the parent's cached snapshot so later
      // reads return the fresh child object, without waking its subscribers.
      this.invalidateChildList(nextParent, false);
    }

    // This block's OWN child list. `blockIds` carries by reference through
    // the context's spread-merge, so a reference change means a real
    // add/remove/reorder. A brand-new block also invalidates: a snapshot may
    // have been cached as empty before the parent itself was hydrated.
    if (!prev || prev.blockIds !== block.blockIds) {
      this.invalidateChildList(block._id, true);
    }

    this.scheduleFlush();
  }

  /** Remove one block. The healed parent (blockIds minus the removed id) is
   *  written separately by the provider via upsert(). */
  remove(blockId: string): void {
    const prev = this.blocks.get(blockId);
    if (!prev) return;

    this.blocks.delete(blockId);
    this.dirtyBlocks.add(blockId);

    if (isPageLike(prev)) this.invalidatePages();
    if (prev.parentId) this.invalidateChildList(prev.parentId, true);
    // Subscribers to the REMOVED block's children see an empty list now.
    this.invalidateChildList(blockId, true);

    this.scheduleFlush();
  }

  /** Replace the entire store (hydration / workspace switch). Notifies every
   *  subscriber — a full swap invalidates all slices by definition. */
  replaceAll(blocks: Block[]): void {
    this.blocks = new Map(blocks.map((b) => [b._id, b]));
    this.childSnapshots.clear();
    this.pagesSnapshot = null;
    this.notifyAllPending = true;
    this.scheduleFlush();
  }

  // ─── Invalidation + coalesced flush ──────────────────────────────

  private invalidateChildList(parentId: string, notifySubscribers: boolean): void {
    this.childSnapshots.delete(parentId);
    if (notifySubscribers) this.dirtyChildLists.add(parentId);
  }

  private invalidatePages(): void {
    this.pagesSnapshot = null;
    this.pagesDirty = true;
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => this.flush());
  }

  private flush(): void {
    this.flushScheduled = false;

    // Snapshot + clear pending state BEFORE emitting, so a subscriber that
    // writes back into the store during notify schedules a fresh flush
    // instead of mutating the sets we are iterating.
    const notifyAll = this.notifyAllPending;
    const dirtyBlocks = this.dirtyBlocks;
    const dirtyChildLists = this.dirtyChildLists;
    const pagesDirty = this.pagesDirty;
    this.notifyAllPending = false;
    this.dirtyBlocks = new Set();
    this.dirtyChildLists = new Set();
    this.pagesDirty = false;

    if (notifyAll) {
      for (const set of this.blockListeners.values()) emit(set);
      for (const set of this.childListeners.values()) emit(set);
      emit(this.pageListeners);
      return;
    }

    for (const blockId of dirtyBlocks) emit(this.blockListeners.get(blockId));
    for (const parentId of dirtyChildLists) emit(this.childListeners.get(parentId));
    if (pagesDirty) emit(this.pageListeners);
  }
}

/** App-wide singleton. GlobalBlockProvider dual-writes into this instance;
 *  useBlock / useChildBlocks / usePages read from it. */
export const blockStore = new BlockStore();
